/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { v4 as uuid } from "uuid";
import { useAppSnackbar } from "../Components/AppSnackbarContext";
import InputBaseProps from "../Components/InputBase/InputBaseProps";

const FELD_BEZEICHNUNG_SEP = uuid();

type ChangeHandler = (...args: any) => void;

type InputBase = Pick<
    InputBaseProps,
    "errors" | "typBezeichnung" | "feldBezeichnung" | "keepErrorsOnChange"
>;

function getFeldBezeichnungString(feldBezeichnung: string | string[]): string {
    if (typeof feldBezeichnung === "string") {
        if (feldBezeichnung.includes(FELD_BEZEICHNUNG_SEP)) {
            throw new Error(`Feldbezeichnung "${feldBezeichnung}" enthält ungültiges Zeichen ${FELD_BEZEICHNUNG_SEP}.`);
        }

        return feldBezeichnung;
    }

    return feldBezeichnung.map(getFeldBezeichnungString).join(FELD_BEZEICHNUNG_SEP);
}

export default function useErrors(inputBase: InputBase) {
    const { hasItems, itemsToArray, clearByFieldPath } = useAppSnackbar();

    const { errors, typBezeichnung, feldBezeichnung, keepErrorsOnChange } = inputBase;

    const error = !!errors?.length || hasItems(typBezeichnung, feldBezeichnung)
    const errorTexts = errors ?? itemsToArray(typBezeichnung, feldBezeichnung);

    // Wenn als Feldbezeichnung ein Array verwendet wird, ändert sich beim erneuten Rendern
    // die Identität der Feldbezeichnung und das obwohl die Feldbezeichnung an sich konstant ist.
    // Das kann zu Endlosschleifen führen, wenn `clear` in einem Effekt verwendet wird. Um dies
    // zu verhindern wird die Feldbezeichnung immer in einen String umgewandelt.
    const feldBezeichungString = getFeldBezeichnungString(feldBezeichnung);
    const clear = React.useCallback(
        () => clearByFieldPath(typBezeichnung, feldBezeichungString.split(FELD_BEZEICHNUNG_SEP)),
        [clearByFieldPath, feldBezeichungString, typBezeichnung]);

    const clearErrorsOnChange = React.useCallback(
        <T extends ChangeHandler>(handler?: T) =>
            (...args: Parameters<T>) => {
                handler?.(...args);
                if (!keepErrorsOnChange) {
                    clear();
                }
            },
        [clear, keepErrorsOnChange]);

    return { error, errorTexts, clear, clearErrorsOnChange };
}
